import { Button, Form } from "antd";
import { useIntl } from "react-intl";
import { useParams } from "react-router";
import { FormItem } from "~components/Form/FormItem";
import { FormSelect } from "~components/Form/FormSelect";
import type { InfinitySelectOption } from "~components/InfinitySelect";
import SelectRolePerson from "~components/SelectRolePerson/SelectRolePerson";
import { RoleStatuses } from "~enums/roleStatuses";
import type { Roles } from "~enums/roles";
import type { CreateRole } from "~hooks/apiHooks/useCreateRole";
import { useSportConfiguration } from "~hooks/useSportConfiguration";
import { getRoleTranslationKey } from "~utilities/getRoleTranslationKey";
import styles from "./AssignFixtureOfficialsForm.module.css";

type PersonValue = (InfinitySelectOption & { value: { personId: string; role: string } }) | null;

type FormType = {
  person: PersonValue;
  role: Roles;
  status: RoleStatuses;
};

type Props = {
  createRole: (payload: CreateRole) => Promise<unknown>;
  fixtureId: string | null;
  isParentLoading: boolean;
};

export const AssignFixtureOfficialsForm = ({ createRole, fixtureId, isParentLoading }: Props) => {
  const { formatMessage } = useIntl();

  const { seasonId = "", sport = "" } = useParams();

  const { fixtureOfficialRoles, seasonOfficialRoles } = useSportConfiguration();
  const [form] = Form.useForm<FormType>();

  const onSubmit = (values: FormType) => {
    createRole({
      fixtureId: fixtureId ?? "",
      personId: values.person?.value?.personId ?? "",
      role: values.role,
      status: values.status,
      seasonId,
    }).then(() => form.resetFields());
  };

  const isLoading = isParentLoading;

  const sharedSelectProps = {
    className: styles.select,
    placeholder: formatMessage({ id: "person" }),
    disabled: isLoading,
    rules: [
      {
        required: true,
        message: "",
      },
    ],
  };

  return (
    <Form form={form} onFinish={onSubmit} className={styles.grid}>
      <FormItem name="person" style={{ minWidth: 200 }} layout="vertical">
        <SelectRolePerson
          disabled={isLoading}
          placeholder={formatMessage({ id: "person" })}
          allowClear={true}
          seasonId={seasonId}
          roles={seasonOfficialRoles}
          onChange={(value: unknown) => {
            if (fixtureOfficialRoles.find((role) => role === (value as PersonValue)?.value?.role)) {
              form.setFieldsValue({
                role: (value as PersonValue)?.value?.role as Roles,
              });
            } else {
              form.setFieldsValue({ role: undefined });
            }
            form.setFieldsValue({ status: RoleStatuses.Active });
          }}
        />
      </FormItem>
      <FormSelect
        name="role"
        selectProps={{
          options: fixtureOfficialRoles.map((role) => ({
            label: formatMessage({
              id: `roles.${getRoleTranslationKey(role, sport)}`,
            }),
            value: role,
          })),
          placeholder: formatMessage({ id: "role" }),
        }}
        {...sharedSelectProps}
      />
      <FormSelect
        name="status"
        selectProps={{
          options: Object.values(RoleStatuses).map((status) => ({
            label: formatMessage({ id: `roleStatus.${status}` }),
            value: status,
          })),
          placeholder: formatMessage({ id: "status" }),
        }}
        {...sharedSelectProps}
      />
      <Button htmlType="submit" type="primary" disabled={isLoading} icon={<i className="fas fa-plus" />}>
        {formatMessage({ id: "assign" })}
      </Button>
    </Form>
  );
};
